/**
 * CommsTimelinePanel — Contact Spine Phase 2
 * -----------------------------------------------
 * Merged timeline of a client's SMS + calls + voicemails, rendered
 * as an inline conversation with chip-style call/voicemail markers
 * interleaved between the SMS bubbles.
 *
 * Props
 * -----
 *   patientId   — client uuid; used to resolve the linked contact
 *                 server-side via /api/contact-registry/timeline/by-patient
 *   days        — history window (defaults 90)
 *
 * Design notes
 * ------------
 * * SMS bubbles are wide (max-w-md) chat-style bubbles.
 * * Call and voicemail rows are compact horizontal chips so the
 *   text conversation stays visually dominant.
 * * Direction (inbound vs outbound) drives alignment (right vs left)
 *   AND colour, matching the SMS thread pattern users already know.
 * * Zero external state — pure fetch on mount + filter by kind.
 */
import { useEffect, useMemo, useState } from "react";
import { MessageSquare, Phone, Voicemail, RefreshCw, FileText, Mail } from "lucide-react";
import MmsMedia from "../comms/MmsMedia";

const API = process.env.REACT_APP_BACKEND_URL;
const authHdr = () => {
  const tok = localStorage.getItem("token");
  return tok ? { Authorization: `Bearer ${tok}` } : {};
};

function fmtTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  });
}

function fmtDuration(sec) {
  if (!sec) return "0s";
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}m ${s}s`;
}

/* ─── Individual row renderers ──────────────────────────────────── */

function SmsBubble({ e }) {
  const outbound = e.direction === "outbound";
  const media = Array.isArray(e.media_urls) ? e.media_urls : [];
  const hasMedia = media.length > 0;
  // Contact-registry timeline events expose `id` (event uuid) but the
  // proxy needs the underlying SMS row id or message_sid. Prefer any
  // explicit sms_id / message_sid field, fall back to `id`.
  const mediaKey = e.sms_id || e.message_sid || e.id || "";
  return (
    <div
      className={`flex ${outbound ? "justify-end" : "justify-start"} mb-2`}
      data-testid={`comms-sms-${e.id}`}
    >
      <div className={`max-w-md rounded-2xl px-4 py-2.5 shadow-sm ${
        outbound
          ? "bg-blue-600 text-white rounded-br-sm"
          : "bg-white border border-slate-200 text-slate-900 rounded-bl-sm"
      }`}>
        {hasMedia && (
          <div className="space-y-1.5 mb-1.5" data-testid={`comms-sms-media-${e.id}`}>
            {media.map((url, mi) => (
              <MmsMedia
                key={`${mediaKey}-${mi}`}
                messageId={mediaKey}
                index={mi}
                rawUrl={url}
                outbound={outbound}
              />
            ))}
          </div>
        )}
        {e.body ? (
          <p className="text-sm whitespace-pre-wrap break-words">{e.body}</p>
        ) : !hasMedia ? (
          <p className="text-sm whitespace-pre-wrap break-words">
            <em className="opacity-60">(empty message)</em>
          </p>
        ) : null}
        <div className={`text-[10px] mt-1.5 ${outbound ? "text-blue-100" : "text-slate-400"}`}>
          {fmtTime(e.timestamp)}
        </div>
      </div>
    </div>
  );
}

function CallChip({ e }) {
  const outbound = e.direction === "outbound";
  const missed = e.status === "no-answer" || e.status === "busy" || e.status === "failed";
  return (
    <div
      className="flex justify-center my-2"
      data-testid={`comms-call-${e.id}`}
    >
      <div className={`inline-flex items-center gap-2 text-[11px] font-medium px-3 py-1.5 rounded-full ${
        missed
          ? "bg-red-50 text-red-700 border border-red-200"
          : "bg-slate-100 text-slate-600 border border-slate-200"
      }`}>
        <Phone className="w-3 h-3" />
        <span>
          {missed ? "Missed call" : outbound ? "Outbound call" : "Inbound call"}
          {e.duration > 0 && ` · ${fmtDuration(e.duration)}`}
        </span>
        <span className="text-slate-400 ml-1">· {fmtTime(e.timestamp)}</span>
      </div>
    </div>
  );
}

function VoicemailRow({ e }) {
  return (
    <div className="flex justify-start my-2" data-testid={`comms-vm-${e.id}`}>
      <div className="max-w-md w-full rounded-xl px-3 py-2.5 bg-amber-50 border border-amber-200">
        <div className="flex items-center gap-2 text-[11px] font-semibold text-amber-800 mb-1">
          <Voicemail className="w-3.5 h-3.5" />
          Voicemail · {fmtDuration(e.duration)}
          <span className="text-slate-500 font-normal ml-auto">{fmtTime(e.timestamp)}</span>
        </div>
        {e.transcript ? (
          <p className="text-[13px] text-slate-800 italic">"{e.transcript}"</p>
        ) : (
          <p className="text-[12px] text-slate-500 italic">(no transcription yet)</p>
        )}
        {e.recording && (
          <audio controls src={e.recording} className="mt-2 w-full h-8" preload="none" />
        )}
      </div>
    </div>
  );
}

function FaxRow({ e }) {
  // Phase 3a — indigo cards distinguish faxes from amber voicemails
  // and slate calls. Direction shown as a chip on the header row.
  const outbound = e.direction === "outbound";
  return (
    <div className="flex justify-start my-2" data-testid={`comms-fax-${e.id}`}>
      <div className="max-w-md w-full rounded-xl px-3 py-2.5 bg-indigo-50 border border-indigo-200">
        <div className="flex items-center gap-2 text-[11px] font-semibold text-indigo-800 mb-1">
          <FileText className="w-3.5 h-3.5" />
          {outbound ? "Outbound fax" : "Inbound fax"}
          {e.num_pages > 0 && ` · ${e.num_pages} page${e.num_pages > 1 ? "s" : ""}`}
          <span className="text-slate-500 font-normal ml-auto">{fmtTime(e.timestamp)}</span>
        </div>
        {e.subject && (
          <p className="text-[13px] text-slate-900 font-medium truncate">{e.subject}</p>
        )}
        {e.filename && (
          <p className="text-[11px] text-slate-500 truncate">{e.filename}</p>
        )}
        {e.status && (
          <p className="text-[10px] mt-1 text-indigo-600 uppercase tracking-wide">{e.status}</p>
        )}
      </div>
    </div>
  );
}

function EmailRow({ e }) {
  // Phase 3b — emerald cards for email. Subject line dominant,
  // preview body underneath (already tag-stripped server-side).
  const outbound = e.direction === "outbound";
  return (
    <div className="flex justify-start my-2" data-testid={`comms-email-${e.id}`}>
      <div className="max-w-md w-full rounded-xl px-3 py-2.5 bg-emerald-50 border border-emerald-200">
        <div className="flex items-center gap-2 text-[11px] font-semibold text-emerald-800 mb-1">
          <Mail className="w-3.5 h-3.5" />
          {outbound ? "Outbound email" : "Inbound email"}
          <span className="text-slate-500 font-normal ml-auto">{fmtTime(e.timestamp)}</span>
        </div>
        {e.subject && (
          <p className="text-[13px] text-slate-900 font-semibold truncate">{e.subject}</p>
        )}
        {e.body && (
          <p className="text-[12px] text-slate-700 mt-1 line-clamp-3">{e.body}</p>
        )}
        {e.to && (
          <p className="text-[10px] text-slate-500 mt-1 truncate">To: {e.to}</p>
        )}
      </div>
    </div>
  );
}

/* ─── Filter chip control ───────────────────────────────────────── */

function KindFilters({ value, onChange, counts }) {
  const opts = [
    { k: "all",       label: "All" },
    { k: "sms",       label: "SMS" },
    { k: "call",      label: "Calls" },
    { k: "voicemail", label: "VMs" },
    { k: "fax",       label: "Fax" },
    { k: "email",     label: "Email" },
  ];
  return (
    <div className="flex items-center gap-1.5" data-testid="comms-kind-filters">
      {opts.map(o => (
        <button
          key={o.k}
          onClick={() => onChange(o.k)}
          data-testid={`comms-filter-${o.k}`}
          className={`text-[11px] font-semibold px-2.5 py-1 rounded-full transition-colors ${
            value === o.k
              ? "bg-blue-600 text-white"
              : "bg-slate-100 text-slate-600 hover:bg-slate-200"
          }`}
        >
          {o.label} {typeof counts[o.k] === "number" && (
            <span className={`ml-1 opacity-70`}>({counts[o.k]})</span>
          )}
        </button>
      ))}
    </div>
  );
}

/* ─── Main panel ───────────────────────────────────────────────── */

export default function CommsTimelinePanel({ patientId, contactId, days = 90 }) {
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [kind, setKind] = useState("all");
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      setState({ loading: true, data: null, error: null });
      try {
        // Prefer direct contact_id lookup when caller supplies one
        // (Unified Inbox path). Otherwise resolve via patient_id
        // (Client File path).
        const url = contactId
          ? `${API}/api/contact-registry/${contactId}/timeline?days=${days}&limit=200`
          : `${API}/api/contact-registry/timeline/by-patient/${patientId}?days=${days}&limit=200`;
        const res = await fetch(url, { headers: authHdr() });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled) setState({ loading: false, data, error: null });
      } catch (e) {
        if (!cancelled) setState({ loading: false, data: null, error: e.message });
      }
    }
    if (contactId || patientId) run();
    return () => { cancelled = true; };
  }, [patientId, contactId, days, tick]);

  const events = state.data?.events || [];
  const counts = useMemo(() => {
    const c = { all: events.length, sms: 0, call: 0, voicemail: 0, fax: 0, email: 0 };
    for (const e of events) c[e.kind] = (c[e.kind] || 0) + 1;
    return c;
  }, [events]);

  const filtered = useMemo(() => {
    if (kind === "all") return events;
    return events.filter(e => e.kind === kind);
  }, [events, kind]);

  // Timeline arrives DESC; display DESC (newest at top).
  return (
    <div className="p-4" data-testid="comms-timeline-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-[13px] font-black text-slate-900 tracking-tight">
            Communications
          </h3>
          <p className="text-[10.5px] text-slate-500 mt-0.5">
            Merged SMS, calls, and voicemails from the last {days} days
          </p>
        </div>
        <div className="flex items-center gap-3">
          <KindFilters value={kind} onChange={setKind} counts={counts} />
          <button
            onClick={() => setTick(t => t + 1)}
            title="Refresh"
            className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500"
            data-testid="comms-refresh"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {state.loading && (
        <div className="text-center text-[12px] text-slate-500 py-10">Loading…</div>
      )}

      {state.error && !state.loading && (
        <div className="text-center text-[12px] text-red-600 py-10" data-testid="comms-error">
          Failed to load timeline: {state.error}
        </div>
      )}

      {!state.loading && !state.error && filtered.length === 0 && (
        <div className="text-center py-14 text-slate-500" data-testid="comms-empty">
          <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-30" />
          <p className="text-[13px] font-semibold">No communications yet</p>
          <p className="text-[11px] mt-1">
            {events.length > 0
              ? `Try switching the filter — there are ${events.length} events across other channels.`
              : `No SMS, calls, or voicemails linked to this client in the last ${days} days.`}
          </p>
        </div>
      )}

      {!state.loading && filtered.length > 0 && (
        <div className="space-y-0.5" data-testid="comms-timeline-list">
          {filtered.map(e => {
            if (e.kind === "sms") return <SmsBubble key={e.id} e={e} />;
            if (e.kind === "call") return <CallChip key={e.id} e={e} />;
            if (e.kind === "voicemail") return <VoicemailRow key={e.id} e={e} />;
            if (e.kind === "fax") return <FaxRow key={e.id} e={e} />;
            if (e.kind === "email") return <EmailRow key={e.id} e={e} />;
            return null;
          })}
        </div>
      )}
    </div>
  );
}
